/** Spec-sheet sharing helpers: hand the PDF to the OS share sheet via the Web Share API, with a download fallback (FIR-7498). */

import { clientInstance } from '../../utils/http-client'

const PDF_MIME = 'application/pdf'

/** True when the browser can share the given files via the Web Share API (guards SSR / non-DOM and data-only `navigator.share`). */
export function canShareFiles(files: File[]): boolean {
  if (typeof navigator === 'undefined') return false
  if (typeof navigator.share !== 'function') return false
  if (typeof navigator.canShare !== 'function') return false
  return navigator.canShare({ files })
}

/** Build a filesystem-safe PDF filename from a variant's SKU or name, falling back to the variant id. */
export function buildSpecsheetFilename(variant: {
  id: number
  sku?: string | null
  name?: string | null
}): string {
  const stem = (variant.sku || variant.name || `variant-${variant.id}`)
    .trim()
    .replace(/[^\w.-]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `${stem || `variant-${variant.id}`}-spec-sheet.pdf`
}

/** Fetch the Syndigo spec-sheet PDF for a variant as a blob. */
async function fetchSpecsheetBlob(variantId: number): Promise<Blob> {
  const response = await clientInstance.get(
    `products/catalog/variants/${variantId}/syndigo/specsheet/`,
    { params: { output: 'pdf' }, responseType: 'blob' }
  )
  return response.data as Blob
}

export interface ShareSpecsheetVariant {
  id: number
  sku?: string | null
  name?: string | null
}

/** Share a variant's spec-sheet PDF via the OS share sheet; returns `'unsupported'` (caller downloads instead), `'cancelled'` on user dismissal (AbortError), or `'shared'`. */
export async function shareSpecsheet(
  variant: ShareSpecsheetVariant
): Promise<'shared' | 'unsupported' | 'cancelled'> {
  const filename = buildSpecsheetFilename(variant)

  // Probe file-share capability with a zero-byte sentinel before paying for
  // the PDF fetch, so the desktop/download fallback doesn't fetch it twice.
  const sentinel = new File([], filename, { type: PDF_MIME })
  if (!canShareFiles([sentinel])) {
    return 'unsupported'
  }

  const blob = await fetchSpecsheetBlob(variant.id)
  const file = new File([blob], filename, { type: PDF_MIME })

  const title = variant.name || filename
  try {
    await navigator.share({
      files: [file],
      title,
      text: `Spec sheet: ${title}`,
    })
    return 'shared'
  } catch (err) {
    // The user dismissing the share sheet rejects with AbortError; that's
    // not a failure and must not trigger the download fallback.
    if (err instanceof DOMException && err.name === 'AbortError') {
      return 'cancelled'
    }
    throw err
  }
}
